import { Loading } from "@/components";
import Modal from "@/components/common/Modal";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { getIsLoading, setIsApiLoading, setIsLoading } from "@/store/appSlice";
import API from "@/utils/api";
import { WORD_TYPES, WORD_LEVELS } from "@/utils/constants";
import { yupResolver } from "@hookform/resolvers/yup";
import DT from "datatables.net-dt";
import DataTable from "datatables.net-react";
import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import * as yup from "yup";

DataTable.use(DT);

type Word = {
  id: string;
  alphabetOrder: string;
  displayOrder: string;
  en: string;
  ja: string;
  lessonId: string;
  level: number;
  meanings: Array<{
    ja: string;
    type: string;
  }>;
  no: string;
  read_uk: string;
  read_us: string;
  sampleSentences: Array<{
    en: string;
    ja: string;
  }>;
  word: string;
  mp3Url?: string;
};

const wordSchema = yup.object().shape({
  displayOrder: yup.string().required("表示順が必須です。"),
  en: yup.string().required("英語が必須です。"),
  ja: yup.string().required("日本語が必須です。"),
  level: yup.number().required("レベルが必須です。").oneOf([600, 730, 860, 990], "有効なレベルを選択してください。"),
  alphabetOrder: yup.string(),
  read_uk: yup.string(),
  read_us: yup.string(),
});

const Words = () => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector(getIsLoading);
  
  const [words, setWords] = useState<Word[]>([]);
  const [activeWord, setActiveWord] = useState<Word | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState<string>("");
  
  // Form states for meanings and sample sentences
  const [meanings, setMeanings] = useState<Array<{ ja: string; type: string }>>([]);
  const [sampleSentences, setSampleSentences] = useState<Array<{ en: string; ja: string }>>([]);
  
  // Audio upload states
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [selectedAudioFile, setSelectedAudioFile] = useState<File | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    resolver: yupResolver(wordSchema),
  });

  const fetchWords = async (page = 1) => {
    dispatch(setIsLoading(true));
    try {
      const params = new URLSearchParams();
      params.append('limit', pageSize.toString());
      params.append('page', page.toString());
      
      if (searchTerm) params.append('search', searchTerm);

      const response = await API.get(`words?${params.toString()}`);
      const { data } = response.data;
      
      const processedWords = (data?.data || []).map((word: any) => ({
        id: word.id || "",
        alphabetOrder: word.alphabetOrder || "",
        displayOrder: word.displayOrder || "",
      en: word.en || "",
        ja: word.ja || "",
        lessonId: word.lessonId || "",
        level: word.level || 600,
        meanings: word.meanings || [],
        no: word.no || "",
        read_uk: word.read_uk || "",
        read_us: word.read_us || "",
        sampleSentences: word.sampleSentences || [],
        word: word.en || "",
        mp3Url: word.mp3Url || null,
      }));
      
      setWords(processedWords);
      setCurrentPage(data?.page || 1);
      setTotalPages(data?.totalPages || 0);
      setTotalCount(data?.total || 0);
      
      // Make words available globally for event handlers
      (window as any).words = processedWords;
      (window as any).openDetailModal = openDetailModal;
      (window as any).openEditModal = openEditModal;
      (window as any).handleDeleteWord = handleDeleteWord;
      (window as any).playAudio = playAudio;
    } catch (err) {
      console.error(err);
      toast.error("単語の取得に失敗しました。");
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  // Handle audio file selection
  const handleAudioFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('audio/')) {
        toast.error("音声ファイルを選択してください。");
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error("ファイルサイズは10MB以下にしてください。");
        return;
      }
      
      setSelectedAudioFile(file);
    }
  };

  // Convert file to base64
  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  // Audio playback function
  const playAudio = (word: Word) => {
    console.log(word);
    
    if (!word.mp3Url) {
      toast.warning("音声ファイルが見つかりません。");
      return;
    }
    
    try {
      const audio = new Audio(word.mp3Url);
      audio.volume = 1.0;
      
      audio.play().catch((error) => {
        console.error("MP3 playback failed:", error);
        toast.error("音声の再生に失敗しました。");
      });
      
    } catch (error) {
      console.error("Failed to play audio:", error);
      toast.error("音声の再生に失敗しました。");
    }
  };

  const handleCreateWord = async (data: any) => {
    // Validate MP3 file is required for creation
    if (!selectedAudioFile) {
      toast.error("音声ファイルの選択は必須です。");
      return;
    }

    try {
      dispatch(setIsApiLoading(true));
      setUploadingAudio(true);
      
      // Convert audio file to base64
      const audioBase64 = await convertFileToBase64(selectedAudioFile);
      
      const wordData = {
        ...data,
        meanings,
        sampleSentences,
        audioFile: audioBase64,
      };
      
      await API.post("word", wordData);
      toast.success("単語が作成されました。");
      fetchWords();
      resetForm();
      document.querySelector<HTMLButtonElement>("#modalWord .btn-close")?.click();
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || "単語の作成に失敗しました。");
    } finally {
      dispatch(setIsApiLoading(false));
      setUploadingAudio(false);
    }
  };

  const handleUpdateWord = async (data: any) => {
    if (!activeWord) return;
    
    try {
      dispatch(setIsApiLoading(true));
      setUploadingAudio(true);
      
      const wordData = {
        ...data,
        meanings,
        sampleSentences,
      };

      // Add audio base64 data if file is selected
      if (selectedAudioFile) {
        const audioBase64 = await convertFileToBase64(selectedAudioFile);
        wordData.audioFile = audioBase64;
      }
      
      await API.put(`word/${activeWord.id}`, wordData);
      toast.success("単語が更新されました。");
      fetchWords();
      resetForm();
      document.querySelector<HTMLButtonElement>("#modalWord .btn-close")?.click();
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || "単語の更新に失敗しました。");
    } finally {
      dispatch(setIsApiLoading(false));
      setUploadingAudio(false);
    }
  };

  const handleDeleteWord = async (word: Word) => {
    if (!window.confirm(`「${word.en}」を削除しますか？`)) return;
    
    try {
      dispatch(setIsApiLoading(true));
      await API.delete(`word/${word.id}`);
      toast.success("単語が削除されました。");
      fetchWords();
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || "単語の削除に失敗しました。");
    } finally {
      dispatch(setIsApiLoading(false));
    }
  };

  const openCreateModal = () => {
    setIsEditMode(false);
    setActiveWord(null);
    resetForm();
    
    // Open the modal programmatically
    const modalElement = document.getElementById('modalWord');
    if (modalElement) {
      const modal = new (window as any).bootstrap.Modal(modalElement);
      modal.show();
    }
  };

  const openEditModal = (word: Word) => {
    setIsEditMode(true);
    setActiveWord(word);
    reset({
      displayOrder: word.displayOrder,
      en: word.en,
      ja: word.ja,
      level: word.level,
      alphabetOrder: word.alphabetOrder,
      read_uk: word.read_uk,
      read_us: word.read_us,
    });
    setMeanings(word.meanings || []);
    setSampleSentences(word.sampleSentences || []);
    setSelectedAudioFile(null);
    
    // Open the modal programmatically
    const modalElement = document.getElementById('modalWord');
    if (modalElement) {
      const modal = new (window as any).bootstrap.Modal(modalElement);
      modal.show();
    }
  };

  const openDetailModal = (word: Word) => {
    setActiveWord(word);
  };

  const resetForm = () => {
    reset({
      displayOrder: "",
      en: "",
      ja: "",
      level: 600,
      alphabetOrder: "",
      read_uk: "",
      read_us: "",
    });
    setMeanings([]);
    setSampleSentences([]);
    setSelectedAudioFile(null);
  };

  const addMeaning = () => {
    setMeanings([...meanings, { ja: "", type: "" }]);
  };

  const removeMeaning = (index: number) => {
    setMeanings(meanings.filter((_, i) => i !== index));
  };

  const updateMeaning = (index: number, field: string, value: string) => {
    const updated = [...meanings];
    updated[index] = { ...updated[index], [field]: value };
    setMeanings(updated);
  };

  const addSampleSentence = () => {
    setSampleSentences([...sampleSentences, { en: "", ja: "" }]);
  };

  const removeSampleSentence = (index: number) => {
    setSampleSentences(sampleSentences.filter((_, i) => i !== index));
  };

  const updateSampleSentence = (index: number, field: string, value: string) => {
    const updated = [...sampleSentences];
    updated[index] = { ...updated[index], [field]: value };
    setSampleSentences(updated);
  };

  const handleFilter = () => {
    setCurrentPage(1);
    fetchWords(1);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setCurrentPage(1);
    // Fetch words without search term
    setTimeout(() => fetchWords(1), 0);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchWords(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
    fetchWords(1);
  };

  useEffect(() => {
    fetchWords();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    fetchWords(1);
  }, [pageSize]);

  return (
    <>
      <Helmet>
        <title>単語・熟語管理</title>
      </Helmet>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h4 className="card-title">単語・熟語管理</h4>
                <button
                  className="btn btn-primary"
                  onClick={openCreateModal}
                >
                  <i className="fas fa-plus me-2"></i>
                  新規作成
                </button>
              </div>
              <div className="card-body">
                {/* Search and Controls */}
                <div className="row mb-3">
                  <div className="col-md-6">
                    <Form.Control
                      type="text"
                      placeholder="検索（英語・日本語・表示順・意味・例文）"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleFilter();
                        }
                      }}
                    />
                  </div>
                  <div className="col-md-2">
                    <Form.Select
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                    >
                      <option value="50">50件</option>
                      <option value="100">100件</option>
                      <option value="200">200件</option>
                      <option value="500">500件</option>
                    </Form.Select>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-outline-primary"
                        onClick={handleFilter}
                      >
                        検索
                      </button>
                      <button
                        className="btn btn-outline-secondary"
                        onClick={clearFilters}
                      >
                        クリア
                      </button>
                    </div>
                  </div>
                </div>

                {/* Pagination Info */}
                <div className="row mb-3">
                  <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="text-muted">
                        全{totalCount}件中 {((currentPage - 1) * pageSize) + 1}～{Math.min(currentPage * pageSize, totalCount)}件を表示
                      </div>
                      <div className="text-muted">
                        ページ {currentPage} / {totalPages}
                      </div>
                    </div>
                  </div>
                </div>

                <Loading />
                {!loading && (
                  <div className="table-responsive">
                    <DataTable
                      key={`words-table-${words.length}`}
                      data={words}
                      className="display"
                      options={{
                        autoWidth: true,
                        columnDefs: [
                          { targets: "_all", className: "text-start" },
                        ],
                        stripeClasses: [],
                        order: [[2, "asc"]], // Sort by displayOrder
                        searching: false, // Disable DataTables search since we have custom filters
                        destroy: true,
                        info: false, // Disable DataTables info since we have custom pagination info
                        paging: false, // Disable DataTables pagination since we have custom pagination
                        language: {
                          emptyTable: "データがありません。",
                        },
                      }}
                      columns={[
                        {
                          title: "英語",
                          data: "en",
                          className: "text-start",
                          orderable: true,
                        },
                        {
                          title: "表示順",
                          data: "displayOrder",
                          className: "text-center",
                          orderable: true,
                          width: "80px",
                        },
                        {
                          title: "日本語",
                          data: "ja",
                          className: "text-start",
                          orderable: true,
                        },
                        {
                          title: "レベル",
                          data: "level",
                          className: "text-center",
                          orderable: true,
                          width: "60px",
                        },
                        {
                          title: "品詞",
                          data: "meanings",
                          className: "text-start",
                          orderable: false,
                          render: (data) => {
                            if (!data || !Array.isArray(data) || data.length === 0) return "-";
                            return data.map(m => WORD_TYPES[m.type] || m.type).join(", ");
                          },
                        },
                        {
                          title: "音声",
                          data: null,
                          className: "text-center",
                          orderable: false,
                          width: "60px",
                          render: (data, type, row) => {
                            return `<button class="btn btn-sm btn-outline-success play-audio-btn" data-id="${row.id}" title="音声再生">
                                      <i class="fas fa-volume-up"></i>
                                    </button>`;
                          },
                        },
                        {
                          title: "操作",
                          data: null,
                          orderable: false,
                          className: "text-center",
                          width: "120px",
                          render: (data, type, row) => {
                            return `
                              <div class="d-flex justify-content-center gap-1">
                                <button class="btn btn-sm btn-outline-info detail-word-btn" data-bs-toggle="modal" data-bs-target="#modalWordDetail" data-id="${row.id}" title="詳細">
                                  <i class="fas fa-eye"></i>
                                </button>
                                <button class="btn btn-sm btn-outline-primary edit-word-btn" data-id="${row.id}" title="編集">
                                  <i class="fas fa-edit"></i>
                                </button>
                                <button class="btn btn-sm btn-outline-danger delete-word-btn" data-id="${row.id}" title="削除">
                                  <i class="fas fa-trash"></i>
                                </button>
                              </div>
                            `;
                          },
                        },
                      ]}
                    />
                  </div>
                )}

                {/* Custom Pagination */}
                {!loading && totalPages > 1 && (
                  <div className="d-flex justify-content-center mt-3">
                    <nav aria-label="Page navigation">
                      <ul className="pagination">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(1)}
                            disabled={currentPage === 1}
                          >
                            最初
                          </button>
                        </li>
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            前へ
                          </button>
                        </li>
                        
                        {/* Page numbers */}
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(pageNum)}
                              >
                                {pageNum}
                              </button>
                            </li>
                          );
                        })}
                        
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            次へ
                          </button>
                        </li>
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(totalPages)}
                            disabled={currentPage === totalPages}
                          >
                            最後
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Create/Edit Modal */}
        <div className="modal fade" id="modalWord" tabIndex={-1}>
          <div className="modal-dialog modal-lg" style={{ maxWidth: '800px' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{isEditMode ? "単語編集" : "単語作成"}</h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
              </div>
              <div className="modal-body">
                <Form onSubmit={handleSubmit(isEditMode ? handleUpdateWord : handleCreateWord)}>
            <Form.Group className="mb-3">
              <Form.Label>表示順 <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="例: 0485"
                isInvalid={!!errors.displayOrder}
                {...register("displayOrder")}
              />
              <Form.Control.Feedback type="invalid">
                {errors.displayOrder?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>英語 <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="例: PIN"
                isInvalid={!!errors.en}
                disabled={isEditMode}
                {...register("en")}
              />
              <Form.Control.Feedback type="invalid">
                {errors.en?.message}
              </Form.Control.Feedback>
              {isEditMode && (
                <Form.Text className="text-muted">
                  編集時は英語を変更できません。
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>日本語 <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="例: 暗唱番号、個人識別番号"
                isInvalid={!!errors.ja}
                {...register("ja")}
              />
              <Form.Control.Feedback type="invalid">
                {errors.ja?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>レベル <span className="text-danger">*</span></Form.Label>
              <Form.Select
                isInvalid={!!errors.level}
                {...register("level")}
              >
                {Object.entries(WORD_LEVELS).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.level?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>アルファベット順</Form.Label>
              <Form.Control
                type="text"
                placeholder="例: 02189"
                isInvalid={!!errors.alphabetOrder}
                {...register("alphabetOrder")}
              />
              <Form.Control.Feedback type="invalid">
                {errors.alphabetOrder?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>UK発音</Form.Label>
              <Form.Control
                type="text"
                placeholder="例: /pɪn/"
                {...register("read_uk")}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>US発音</Form.Label>
              <Form.Control
                type="text"
                placeholder="例: /pɪn/"
                {...register("read_us")}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>音声ファイル {!isEditMode && <span className="text-danger">*</span>}</Form.Label>
              <Form.Control
                type="file"
                accept="audio/*"
                onChange={handleAudioFileChange}
              />
              {selectedAudioFile && (
                <Form.Text className="text-success">
                  選択されたファイル: {selectedAudioFile.name}
                </Form.Text>
              )}
              {!isEditMode && (
                <Form.Text className="text-muted">
                  新規作成時は音声ファイルが必須です。
                </Form.Text>
              )}
              {isEditMode && (
                <Form.Text className="text-muted">
                  ファイルを選択しない場合、既存の音声ファイルが保持されます。
                </Form.Text>
              )}
            </Form.Group>

            {/* Meanings Section */}
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label>意味・品詞</Form.Label>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={addMeaning}
                >
                  <i className="fas fa-plus me-1"></i>
                  追加
                </button>
              </div>
              {meanings.map((meaning, index) => (
                <div key={index} className="row mb-2">
                  <div className="col-md-6">
                    <Form.Control
                      type="text"
                      placeholder="日本語の意味"
                      value={meaning.ja}
                      onChange={(e) => updateMeaning(index, "ja", e.target.value)}
                    />
                  </div>
                  <div className="col-md-5">
                    <Form.Select
                      value={meaning.type}
                      onChange={(e) => updateMeaning(index, "type", e.target.value)}
                    >
                      <option value="">品詞を選択</option>
                      {Object.entries(WORD_TYPES).map(([key, value]) => (
                        <option key={key} value={key}>
                          {value}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                  <div className="col-md-1">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removeMeaning(index)}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Sample Sentences Section */}
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label>例文</Form.Label>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={addSampleSentence}
                >
                  <i className="fas fa-plus me-1"></i>
                  追加
                </button>
              </div>
              {sampleSentences.map((sentence, index) => (
                <div key={index} className="row mb-2">
                  <div className="col-md-5">
                    <Form.Control
                      type="text"
                      placeholder="英語の例文"
                      value={sentence.en}
                      onChange={(e) => updateSampleSentence(index, "en", e.target.value)}
                    />
                  </div>
                  <div className="col-md-6">
                    <Form.Control
                      type="text"
                      placeholder="日本語の例文"
                      value={sentence.ja}
                      onChange={(e) => updateSampleSentence(index, "ja", e.target.value)}
                    />
                  </div>
                  <div className="col-md-1">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removeSampleSentence(index)}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                キャンセル
              </button>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={uploadingAudio}
              >
                {uploadingAudio ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    アップロード中...
                  </>
                ) : (
                  isEditMode ? "更新" : "作成"
                )}
              </button>
            </div>
          </Form>
              </div>
            </div>
          </div>
        </div>

        {/* Detail Modal */}
        <div className="modal fade" id="modalWordDetail" tabIndex={-1}>
          <div className="modal-dialog modal-lg" style={{ maxWidth: '800px' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">単語詳細</h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
              </div>
              <div className="modal-body">
                {activeWord && (
                  <div>
                    <div className="mb-3">
                      <strong>表示順:</strong>
                      <div className="mt-1">{activeWord.displayOrder}</div>
                    </div>
                    
                    <div className="mb-3">
                      <strong>英語:</strong>
                      <div className="mt-1">{activeWord.en}</div>
                    </div>
                    
                    <div className="mb-3">
                      <strong>日本語:</strong>
                      <div className="mt-1">{activeWord.ja}</div>
                    </div>
                    
                    <div className="mb-3">
                      <strong>レベル:</strong>
                      <div className="mt-1">{WORD_LEVELS[activeWord.level] || activeWord.level}</div>
                    </div>
                    
                    <div className="mb-3">
                      <strong>アルファベット順:</strong>
                      <div className="mt-1">{activeWord.alphabetOrder || "-"}</div>
                    </div>
                    
                    <div className="mb-3">
                      <strong>UK発音:</strong>
                      <div className="mt-1">{activeWord.read_uk || "-"}</div>
                    </div>
                    
                    <div className="mb-3">
                      <strong>US発音:</strong>
                      <div className="mt-1">{activeWord.read_us || "-"}</div>
                    </div>
                    
                    {activeWord.meanings && activeWord.meanings.length > 0 && (
                      <div className="mb-3">
                        <strong>意味・品詞:</strong>
                        <div className="mt-2">
                          {activeWord.meanings.map((meaning, index) => (
                            <div key={index} className="mb-2 p-2 border rounded bg-light">
                              <div><strong>意味:</strong> {meaning.ja}</div>
                              <div><strong>品詞:</strong> {WORD_TYPES[meaning.type] || meaning.type}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {activeWord.sampleSentences && activeWord.sampleSentences.length > 0 && (
                      <div className="mb-3">
                        <strong>例文:</strong>
                        <div className="mt-2">
                          {activeWord.sampleSentences.map((sentence, index) => (
                            <div key={index} className="mb-2 p-2 border rounded bg-light">
                              <div><strong>英語:</strong> {sentence.en}</div>
                              <div><strong>日本語:</strong> {sentence.ja}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Add event listeners for buttons
document.addEventListener("click", (e) => {
  const target = e.target as HTMLElement;
  
  if (target.closest(".detail-word-btn")) {
    const id = target.closest(".detail-word-btn")?.getAttribute("data-id");
    const word = (window as any).words?.find((w: Word) => w.id === id);
    if (word) {
      (window as any).openDetailModal?.(word);
    }
  }
  
  if (target.closest(".edit-word-btn")) {
    const id = target.closest(".edit-word-btn")?.getAttribute("data-id");
    const word = (window as any).words?.find((w: Word) => w.id === id);
    if (word) {
      (window as any).openEditModal?.(word);
    }
  }
  
  if (target.closest(".delete-word-btn")) {
    const id = target.closest(".delete-word-btn")?.getAttribute("data-id");
    const word = (window as any).words?.find((w: Word) => w.id === id);
    if (word) {
      (window as any).handleDeleteWord?.(word);
    }
  }

  if (target.closest(".play-audio-btn")) {
    const id = target.closest(".play-audio-btn")?.getAttribute("data-id");
    const word = (window as any).words?.find((w: Word) => w.id === id);
    if (word) {
      (window as any).playAudio?.(word);
    }
  }
});

export default Words;